import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { studentsApi, paymentsApi, invoicesApi, reportsApi, callLogsApi, configApi } from '../services/api';
import CurrencySelector from '../components/CurrencySelector';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import BankSelect from '../components/BankSelect';
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  Building2,
  GraduationCap,
  Receipt,
  CreditCard,
  Plus,
  Loader2,
  AlertCircle,
  Trash2,
  Banknote,
  FileDown,
  Send
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Textarea } from '../components/ui/textarea';

const formatCurrency = (amount, currency = 'USD') => {
  if (!amount && amount !== 0) return '-';
  return `${currency} ${Number(amount).toLocaleString()}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const StudentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role } = useAuth();
  const [student, setStudent] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [newPayment, setNewPayment] = useState({
    amount: '',
    currency: 'USD',
    payment_mode: 'cash',
    reference_number: '',
    bank_name: '',
    notes: ''
  });

  // Call logs
  const [callLogs, setCallLogs] = useState([]);
  const [showCallLogDialog, setShowCallLogDialog] = useState(false);
  const [callLogData, setCallLogData] = useState({ feedback: '', expected_payment_date: '', call_date: new Date().toISOString().split('T')[0] });
  const [savingCallLog, setSavingCallLog] = useState(false);

  const [outstandingDetail, setOutstandingDetail] = useState(null);

  // Statement of account: PDF download + email to parent
  const [downloadingLedger, setDownloadingLedger] = useState(false);
  const [showLedgerEmailDialog, setShowLedgerEmailDialog] = useState(false);
  const [ledgerEmailTo, setLedgerEmailTo] = useState('');
  const [sendingLedgerEmail, setSendingLedgerEmail] = useState(false);
  // Emailing parents is an accounts action; view-only roles get the PDF only.
  const canEmailLedger = ['admin', 'manager', 'accountant', 'accountant_assistant'].includes(role);

  // Payment modes this role may record in (accountant assistant = bank only). The
  // backend enforces it; this keeps the form from offering what would 403.
  const [config, setConfig] = useState(null);
  useEffect(() => {
    configApi.get().then((r) => setConfig(r.data)).catch(() => {});
  }, []);
  const modeLabels = { cash: 'Cash', bank_deposit: 'Bank Deposit' };
  const allowedModes = useMemo(
    () => config?.allowed_payment_modes || ['cash', 'bank_deposit'],
    [config]);
  const canRecordCash = allowedModes.includes('cash');
  useEffect(() => {
    if (allowedModes.length && !allowedModes.includes(newPayment.payment_mode)) {
      setNewPayment((p) => ({ ...p, payment_mode: allowedModes[0] }));
    }
  }, [allowedModes, newPayment.payment_mode]);

  const fetchStudent = useCallback(async () => {
    try {
      const [studentRes, ledgerRes, callLogsRes, outstandingRes] = await Promise.all([
        studentsApi.getOne(id),
        reportsApi.studentLedger(id),
        callLogsApi.getAll({ student_id: id, limit: 50 }),
        studentsApi.getOutstanding(id).catch(() => ({ data: null })),
      ]);
      setStudent(studentRes.data);
      setLedger(ledgerRes.data);
      setCallLogs(callLogsRes.data.logs || []);
      setOutstandingDetail(outstandingRes.data);
    } catch (err) {
      toast.error('Failed to load student data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchStudent();
  }, [fetchStudent]);

  const handlePayment = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await paymentsApi.create({
        student_id: id,
        amount: parseFloat(newPayment.amount),
        currency: newPayment.currency,
        payment_mode: newPayment.payment_mode,
        reference_number: newPayment.reference_number || null,
        bank_name: newPayment.bank_name || null,
        notes: newPayment.notes || null
      });
      toast.success('Payment recorded successfully');
      setShowPaymentDialog(false);
      setNewPayment({
        amount: '',
        currency: 'USD',
        payment_mode: allowedModes[0] || 'cash',
        reference_number: '',
        bank_name: '',
        notes: ''
      });
      fetchStudent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveCallLog = async (e) => {
    e.preventDefault();
    setSavingCallLog(true);
    try {
      await callLogsApi.create({
        student_id: id,
        feedback: callLogData.feedback,
        expected_payment_date: callLogData.expected_payment_date,
        call_date: callLogData.call_date
      });
      toast.success('Call log saved');
      setShowCallLogDialog(false);
      setCallLogData({ feedback: '', expected_payment_date: '', call_date: new Date().toISOString().split('T')[0] });
      fetchStudent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save call log');
    } finally {
      setSavingCallLog(false);
    }
  };

  const handleDownloadLedgerPdf = async () => {
    setDownloadingLedger(true);
    try {
      toast.loading('Generating statement...', { id: 'ledger-pdf' });
      const response = await reportsApi.studentLedgerPdf(id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `statement_${student?.student_id || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Statement downloaded', { id: 'ledger-pdf' });
    } catch (err) {
      toast.error('Failed to generate statement PDF', { id: 'ledger-pdf' });
      console.error(err);
    } finally {
      setDownloadingLedger(false);
    }
  };

  const openLedgerEmailDialog = () => {
    setLedgerEmailTo(student?.parent_email || student?.email || '');
    setShowLedgerEmailDialog(true);
  };

  const handleEmailLedger = async (e) => {
    e.preventDefault();
    setSendingLedgerEmail(true);
    try {
      const res = await reportsApi.emailStudentLedger(id, ledgerEmailTo.trim());
      toast.success(res.data?.message || 'Statement emailed to parent');
      setShowLedgerEmailDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to email statement');
    } finally {
      setSendingLedgerEmail(false);
    }
  };

  const handleDeleteCallLog = async (logId) => {
    try {
      await callLogsApi.delete(logId);
      toast.success('Call log deleted');
      setCallLogs(prev => prev.filter(l => l.id !== logId));
    } catch (err) {
      toast.error('Failed to delete call log');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#3E3F96]" />
      </div>
    );
  }

  if (!student) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertCircle className="w-12 h-12 text-[#C85A40] mb-4" />
        <p className="text-[#5C635F]">Student not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/students')}>
          Back to Students
        </Button>
      </div>
    );
  }

  const pendingInvoices = student.invoices?.filter(i => i.status !== 'paid') || [];
  // Authoritative outstanding from the backend: includes auto-surcharge on overdue
  // invoices and subtracts unallocated payments and credit notes. Clamped to >=0.
  // `raw_outstanding` may be negative when the student is in credit.
  const totalOutstanding = outstandingDetail
    ? Number(outstandingDetail.total_outstanding ?? 0)
    : pendingInvoices.reduce((sum, i) => sum + (i.outstanding || 0), 0);
  const rawOutstanding = outstandingDetail ? Number(outstandingDetail.raw_outstanding ?? 0) : totalOutstanding;
  const creditBalance = rawOutstanding < 0 ? Math.abs(rawOutstanding) : 0;

  // Total Paid / Total Invoiced must be currency-normalised (USD). Payments and
  // invoices can be in UGX/RWF/USD, so summing raw `amount` mixes currencies and
  // makes a single UGX receipt blow up the "Total Paid" box. The ledger entries
  // are already converted to USD by the backend, so derive both totals from it.
  const ledgerEntries = ledger?.ledger || [];
  const totalPaidUsd = ledgerEntries
    .filter(e => e.type === 'payment')
    .reduce((s, e) => s + Number(e.credit || 0), 0);
  const totalInvoicedUsd = ledgerEntries
    .filter(e => e.type === 'invoice')
    .reduce((s, e) => s + Number(e.debit || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/students')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Student Info Card */}
      <Card className="border-[#D8D9E8]">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-[#3E3F96] rounded-full flex items-center justify-center text-white text-2xl font-bold">
                {student.first_name?.charAt(0)}{student.last_name?.charAt(0)}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#1C1F1D]">
                  {student.first_name} {student.last_name}
                </h1>
                <p className="text-[#3E3F96] font-medium">{student.student_id}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge className="bg-[#ECEDF5] text-[#3E3F96]">
                    <GraduationCap className="w-3 h-3 mr-1" />
                    {student.current_class}
                  </Badge>
                  <Badge variant="outline">
                    <Building2 className="w-3 h-3 mr-1" />
                    {student.branch}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-[#3E3F96] hover:bg-[#2D2E7A]" data-testid="collect-payment-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Collect Payment
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Record Payment</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handlePayment} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Amount</Label>
                        <Input
                          type="number"
                          value={newPayment.amount}
                          onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                          required
                          min="0"
                          step="0.01"
                          data-testid="payment-amount"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Currency</Label>
                        <CurrencySelector
                          value={newPayment.currency}
                          onChange={(v) => setNewPayment({ ...newPayment, currency: v })}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Payment Mode</Label>
                      <Select
                        value={newPayment.payment_mode}
                        onValueChange={(v) => setNewPayment({ ...newPayment, payment_mode: v })}
                        disabled={allowedModes.length < 2}
                      >
                        <SelectTrigger data-testid="payment-mode">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {allowedModes.map((m) => (
                            <SelectItem key={m} value={m}>{modeLabels[m] || m.replace(/_/g, ' ')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!canRecordCash && (
                        <p className="text-xs text-[#5C635F]" data-testid="payment-mode-restricted">
                          Your role records bank payments only.
                        </p>
                      )}
                    </div>

                    {newPayment.payment_mode === 'bank_deposit' && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Bank Name</Label>
                          <BankSelect
                            value={newPayment.bank_name}
                            onChange={(v) => setNewPayment({ ...newPayment, bank_name: v })}
                            testId="payment-bank"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Reference Number</Label>
                          <Input
                            value={newPayment.reference_number}
                            onChange={(e) => setNewPayment({ ...newPayment, reference_number: e.target.value })}
                            data-testid="payment-reference"
                          />
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label>Notes (Optional)</Label>
                      <Input
                        value={newPayment.notes}
                        onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                        data-testid="payment-notes"
                      />
                    </div>

                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => setShowPaymentDialog(false)}>
                        Cancel
                      </Button>
                      <Button 
                        type="submit" 
                        className="bg-[#3E3F96] hover:bg-[#2D2E7A]"
                        disabled={submitting}
                        data-testid="submit-payment-btn"
                      >
                        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Record Payment
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-[#D8D9E8]">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#F5F5FA] rounded-lg">
                <User className="w-4 h-4 text-[#5C635F]" />
              </div>
              <div>
                <p className="text-xs text-[#5C635F]">Parent/Guardian</p>
                <p className="font-medium text-[#1C1F1D]">{student.parent_guardian}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#F5F5FA] rounded-lg">
                <Mail className="w-4 h-4 text-[#5C635F]" />
              </div>
              <div>
                <p className="text-xs text-[#5C635F]">Email</p>
                <p className="font-medium text-[#1C1F1D]">{student.parent_email || student.email || '-'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#F5F5FA] rounded-lg">
                <Phone className="w-4 h-4 text-[#5C635F]" />
              </div>
              <div>
                <p className="text-xs text-[#5C635F]">Phone</p>
                <p className="font-medium text-[#1C1F1D]">{student.phone || '-'}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Financial Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {(() => {
          const opening = Number(outstandingDetail?.opening_balance ?? student.balance ?? 0);
          const isCredit = opening < 0;
          return (
            <Card className="border-[#D8D9E8]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${isCredit ? 'bg-[#1A4331]/10' : opening > 0 ? 'bg-[#D99C3B]/10' : 'bg-[#5C635F]/10'}`}>
                    <Banknote className={`w-5 h-5 ${isCredit ? 'text-[#1A4331]' : opening > 0 ? 'text-[#D99C3B]' : 'text-[#5C635F]'}`} />
                  </div>
                  <div>
                    <p className="text-sm text-[#5C635F]">Opening Balance</p>
                    <p className={`text-xl font-bold tabular-nums ${isCredit ? 'text-[#1A4331]' : opening > 0 ? 'text-[#D99C3B]' : 'text-[#1C1F1D]'}`}>
                      {opening === 0 ? formatCurrency(0) : (isCredit ? `(${formatCurrency(Math.abs(opening))})` : formatCurrency(opening))}
                    </p>
                    {isCredit && <p className="text-xs text-[#1A4331]">Credit carried forward</p>}
                    {opening > 0 && <p className="text-xs text-[#D99C3B]">Owed from prior year</p>}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })()}
        <Card className="border-[#D8D9E8]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#5556A8]/10 rounded-lg">
                <Receipt className="w-5 h-5 text-[#5556A8]" />
              </div>
              <div>
                <p className="text-sm text-[#5C635F]">Total Invoiced</p>
                <p className="text-xl font-bold text-[#1C1F1D] tabular-nums">
                  {formatCurrency(totalInvoicedUsd)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#D8D9E8]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#3E3F96]/10 rounded-lg">
                <CreditCard className="w-5 h-5 text-[#3E3F96]" />
              </div>
              <div>
                <p className="text-sm text-[#5C635F]">Total Paid</p>
                <p className="text-xl font-bold text-[#5556A8] tabular-nums">
                  {formatCurrency(totalPaidUsd)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#D8D9E8] border-l-4 border-l-[#C85A40]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${creditBalance > 0 ? 'bg-[#1A4331]/10' : 'bg-[#C85A40]/10'}`}>
                <AlertCircle className={`w-5 h-5 ${creditBalance > 0 ? 'text-[#1A4331]' : 'text-[#C85A40]'}`} />
              </div>
              <div>
                <p className="text-sm text-[#5C635F]">Outstanding</p>
                {creditBalance > 0 ? (
                  <>
                    <p className="text-xl font-bold text-[#1A4331] tabular-nums">{formatCurrency(0)}</p>
                    <p className="text-xs text-[#1A4331]">Credit balance: {formatCurrency(creditBalance)}</p>
                  </>
                ) : (
                  <p className="text-xl font-bold text-[#C85A40] tabular-nums">
                    {formatCurrency(totalOutstanding)}
                  </p>
                )}
                {creditBalance === 0 && outstandingDetail?.surcharge_owed > 0 && (
                  <p className="text-xs text-[#D99C3B]">
                    incl. surcharge {formatCurrency(outstandingDetail.surcharge_owed)}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="ledger" className="space-y-4">
        <TabsList className="bg-[#ECEDF5]">
          <TabsTrigger value="ledger" data-testid="tab-ledger">Ledger</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="discounts" data-testid="tab-discounts">Discounts</TabsTrigger>
          <TabsTrigger value="call-logs" data-testid="tab-call-logs">Call Logs ({callLogs.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="ledger">
          <Card className="border-[#D8D9E8]">
            <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
              <CardTitle className="text-lg">Account Ledger</CardTitle>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadLedgerPdf}
                  disabled={downloadingLedger}
                  data-testid="ledger-pdf-btn"
                >
                  {downloadingLedger
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <FileDown className="w-4 h-4 mr-2" />}
                  Download PDF
                </Button>
                {canEmailLedger && (
                  <Button
                    size="sm"
                    className="bg-[#3E3F96] hover:bg-[#2D2E7A]"
                    onClick={openLedgerEmailDialog}
                    data-testid="ledger-email-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Email to Parent
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F5FA]">
                      <TableHead className="text-xs uppercase font-semibold">Date</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Invoice Date</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Due Date</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Description</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Reference</TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-right">Debit</TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-right">Credit</TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ledger?.ledger?.map((entry, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-[#5C635F]">{formatDate(entry.date)}</TableCell>
                        <TableCell className="text-[#5C635F]">{entry.invoice_date ? formatDate(entry.invoice_date) : '—'}</TableCell>
                        <TableCell className="text-[#5C635F]">{entry.due_date ? formatDate(entry.due_date) : '—'}</TableCell>
                        <TableCell className="font-medium">{entry.description}</TableCell>
                        <TableCell className="text-[#3E3F96]">{entry.reference}</TableCell>
                        <TableCell className="text-right tabular-nums text-[#C85A40]">
                          {entry.debit > 0 ? formatCurrency(entry.debit) : '-'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-[#5556A8]">
                          {entry.credit > 0 ? formatCurrency(entry.credit) : '-'}
                        </TableCell>
                        <TableCell className={`text-right tabular-nums font-medium ${entry.balance > 0 ? 'text-[#C85A40]' : 'text-[#5556A8]'}`}>
                          {formatCurrency(entry.balance)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Dialog open={showLedgerEmailDialog} onOpenChange={setShowLedgerEmailDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Email Statement to Parent</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleEmailLedger} className="space-y-4">
                <p className="text-sm text-[#5C635F]">
                  The statement of account for <strong>{student.first_name} {student.last_name}</strong>
                  {' '}({student.student_id}) will be sent as a PDF attachment.
                </p>
                <div className="space-y-2">
                  <Label>Send To</Label>
                  <Input
                    type="email"
                    value={ledgerEmailTo}
                    onChange={(e) => setLedgerEmailTo(e.target.value)}
                    placeholder="parent@example.com"
                    required
                    data-testid="ledger-email-to"
                  />
                  {!(student.parent_email || student.email) && (
                    <p className="text-xs text-[#C85A40]">
                      No parent email on this student record — type the address to send to.
                    </p>
                  )}
                </div>
                <div className="rounded-md bg-[#F5F5FA] p-3 text-sm">
                  <span className="text-[#5C635F]">Closing balance: </span>
                  <span className="font-semibold tabular-nums">
                    {formatCurrency(Math.abs(Number(ledger?.final_balance || 0)))}
                    {Number(ledger?.final_balance || 0) < 0 ? ' (credit)' : ''}
                  </span>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowLedgerEmailDialog(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-[#3E3F96] hover:bg-[#2D2E7A]"
                    disabled={sendingLedgerEmail}
                    data-testid="ledger-email-send"
                  >
                    {sendingLedgerEmail
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                      : <><Send className="w-4 h-4 mr-2" />Send Statement</>}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="invoices">
          <Card className="border-[#D8D9E8]">
            <CardHeader>
              <CardTitle className="text-lg">Invoices</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F5FA]">
                      <TableHead className="text-xs uppercase font-semibold">Invoice #</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Invoice Date</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Due Date</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Fee Head</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Term</TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-right">Amount</TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-right">Paid</TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-right">Outstanding</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {student.invoices?.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-medium text-[#3E3F96]">{invoice.invoice_number}</TableCell>
                        <TableCell className="text-[#5C635F]">{invoice.created_at ? formatDate(invoice.created_at) : '—'}</TableCell>
                        <TableCell className="text-[#5C635F]">{invoice.due_date ? formatDate(invoice.due_date) : '—'}</TableCell>
                        <TableCell className="capitalize">{invoice.fee_head}</TableCell>
                        <TableCell>{invoice.term}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatCurrency(invoice.amount)}</TableCell>
                        <TableCell className="text-right tabular-nums text-[#5556A8]">{formatCurrency(invoice.paid_amount)}</TableCell>
                        <TableCell className="text-right tabular-nums text-[#C85A40]">{formatCurrency(invoice.outstanding)}</TableCell>
                        <TableCell>
                          <Badge className={
                            invoice.status === 'paid' ? 'bg-[#5556A8]/10 text-[#5556A8]' :
                            invoice.status === 'partial' ? 'bg-[#D99C3B]/10 text-[#D99C3B]' :
                            'bg-gray-100 text-gray-600'
                          }>
                            {invoice.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments">
          <Card className="border-[#D8D9E8]">
            <CardHeader>
              <CardTitle className="text-lg">Payment History</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F5FA]">
                      <TableHead className="text-xs uppercase font-semibold">Receipt #</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Date</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Mode</TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-right">Amount</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Allocations</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {student.payments?.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell className="font-medium text-[#3E3F96]">{payment.receipt_number}</TableCell>
                        <TableCell className="text-[#5C635F]">{formatDate(payment.created_at)}</TableCell>
                        <TableCell className="capitalize">{payment.payment_mode?.replace('_', ' ')}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium text-[#5556A8]">
                          {formatCurrency(payment.amount, payment.currency)}
                        </TableCell>
                        <TableCell>
                          {payment.allocations?.map((a, i) => (
                            <span key={i} className="inline-block mr-2 text-sm">
                              <Badge variant="outline" className="text-xs">
                                {a.fee_head}: {formatCurrency(a.amount)}
                              </Badge>
                            </span>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="discounts">
          <Card className="border-[#D8D9E8]">
            <CardHeader>
              <CardTitle className="text-lg">Discounts</CardTitle>
            </CardHeader>
            <CardContent>
              {student.discounts && student.discounts.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {student.discounts.map((disc, idx) => (
                    <div key={disc.id || idx} className="p-4 bg-[#F5F5FA] rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium capitalize">{(disc.discount_type || '').replace(/_/g, ' ')}</p>
                        <Badge className={disc.status === 'approved' ? 'bg-[#3E3F96]/10 text-[#3E3F96]' : disc.status === 'rejected' ? 'bg-[#C85A40]/10 text-[#C85A40]' : 'bg-[#D99C3B]/10 text-[#D99C3B]'}>
                          {disc.status}
                        </Badge>
                      </div>
                      <p className="text-xl font-bold text-[#3E3F96]">{disc.percentage}%</p>
                      <p className="text-xs text-[#5C635F] mt-1">{disc.reason}</p>
                      <p className="text-xs text-[#5C635F]">{disc.academic_year}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#5C635F]">No discounts applied</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="call-logs">
          <Card className="border-[#D8D9E8]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Call Logs</CardTitle>
              <Dialog open={showCallLogDialog} onOpenChange={setShowCallLogDialog}>
                <Button size="sm" className="bg-[#3E3F96] hover:bg-[#2D2E7A]" onClick={() => setShowCallLogDialog(true)} data-testid="add-call-log-btn">
                  <Plus className="w-4 h-4 mr-1" />Log Call
                </Button>
                <DialogContent className="max-w-md">
                  <DialogHeader><DialogTitle>Log Call</DialogTitle></DialogHeader>
                  <form onSubmit={handleSaveCallLog} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Call Date</Label>
                      <Input type="date" value={callLogData.call_date} onChange={e => setCallLogData({ ...callLogData, call_date: e.target.value })} data-testid="detail-call-date" />
                    </div>
                    <div className="space-y-2">
                      <Label>Feedback / Comments</Label>
                      <Textarea placeholder="Enter call feedback..." value={callLogData.feedback} onChange={e => setCallLogData({ ...callLogData, feedback: e.target.value })}
                        rows={3} data-testid="detail-call-feedback" />
                    </div>
                    <div className="space-y-2">
                      <Label>Expected Payment Date</Label>
                      <Input type="date" value={callLogData.expected_payment_date} onChange={e => setCallLogData({ ...callLogData, expected_payment_date: e.target.value })} data-testid="detail-call-payment-date" />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setShowCallLogDialog(false)}>Cancel</Button>
                      <Button type="submit" className="bg-[#3E3F96] hover:bg-[#2D2E7A]" disabled={savingCallLog || !callLogData.feedback} data-testid="detail-save-call-btn">
                        {savingCallLog && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F5FA]">
                      <TableHead className="text-xs uppercase font-semibold">Call Date</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Feedback</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Expected Payment</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Logged By</TableHead>
                      <TableHead className="text-xs uppercase font-semibold w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {callLogs.map(log => (
                      <TableRow key={log.id} data-testid={`call-log-row-${log.id}`}>
                        <TableCell className="text-[#5C635F]">{formatDate(log.call_date)}</TableCell>
                        <TableCell className="max-w-xs">{log.feedback}</TableCell>
                        <TableCell className="text-[#3E3F96] font-medium">{log.expected_payment_date ? formatDate(log.expected_payment_date) : '-'}</TableCell>
                        <TableCell className="text-sm text-[#5C635F]">{log.logged_by_name}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteCallLog(log.id)} className="text-red-500 hover:text-red-700">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {callLogs.length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center text-[#5C635F] py-8">No call logs yet</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StudentDetailPage;
